/**
 * @file 本1冊の「読んだページ」のバーと、今週の範囲の印を出す。
 *
 * @what: 本のページ数ぶんの細いバーを置き、読んだページ (閲覧位置の pages_read) の位置を塗る。計画に入っている
 *   本には、今週に読む範囲をバーの上の枠で示す。数字と割合は出さない (バーの見た目だけ)。読み上げには
 *   「読んだページのバー」と、今週の範囲のページだけを載せる。
 * @why: 取り組んでいる本の進みを、達成率の数字ではなく塗りで見せる (代表の判断、2026-09-13)。塗りは
 *   CSS の linear-gradient で1つの要素に表示し、ページの数だけ要素を作らない。
 * @failure: 描画のみ。ページ数が無い資料では出さない (呼び手が判定する)。
 */

import type { CSSProperties } from "react";
import { pageRuns, runsToGradient } from "../model/readPages";
import "./study.css";

export function ReadPagesStrip({
  pageCount,
  pagesRead,
  weekRange,
  printedPage = (p) => p,
}: {
  pageCount: number;
  /** 読んだページ (連番、昇順)。 */
  pagesRead: readonly number[];
  /** 今週に読む範囲 (連番、閉区間)。計画に入っていない本では無し。 */
  weekRange?: { from: number; to: number } | undefined;
  /** 連番を紙に刷られた番号に直す (読み上げの文言に使う)。 */
  printedPage?: (readerPage: number) => number;
}) {
  const runs = pageRuns(pagesRead, pageCount);
  const style: CSSProperties = { backgroundImage: runsToGradient(runs, pageCount) };
  const rangeStyle: CSSProperties | null = weekRange
    ? {
        left: `${(((weekRange.from - 1) / pageCount) * 100).toFixed(2)}%`,
        width: `${(((weekRange.to - weekRange.from + 1) / pageCount) * 100).toFixed(2)}%`,
      }
    : null;
  const rangeText = weekRange
    ? `今週の範囲 p.${printedPage(weekRange.from)}〜p.${printedPage(weekRange.to)}`
    : null;
  return (
    <div className="study-read-strip-wrap">
      <div
        className="study-read-strip"
        role="img"
        aria-label={rangeText ? `読んだページのバー (${rangeText})` : "読んだページのバー"}
        style={style}
      >
        {rangeStyle ? <span className="study-read-strip-week" style={rangeStyle} aria-hidden="true" /> : null}
      </div>
      {rangeText ? <span className="study-read-strip-caption">{rangeText}</span> : null}
    </div>
  );
}
